import { ApolloError, QueryResult, useMutation, useQuery } from '@apollo/client';
import { Button, Tabs, useFlashMessage } from '@indigo-ag/component-module';
import {
  AgFinancing,
  AgStateName,
  BackgroundCheckGroup,
  CollateralType,
  CreditApprovalData,
  FinancingBrazilGrowerLead,
  OpportunityContactRole,
  OpportunitySaleType,
  OpportunityType,
  SerasaReport,
  RiskClassificationResult,
  BacenReport
} from '@indigo-ag/schema';
import i18next from 'core/i18n';
import React, { useContext, useEffect, useState } from 'react';
import { Redirect, Route, Switch, useHistory, useParams, useRouteMatch } from 'react-router-dom';

import { NewPage } from 'core/components';
import { UserContext } from 'core/components/GateWay/GateWay';
import { LoadingIndicator, LoadingWrapper, Permissions } from 'shared';
import { sessionStorageUtils, text } from 'shared/utils';
import { BrazilGrowerGeneralInformation } from '../components/BrazilGrowerContacts/BrazilGrowerGeneralInfo';
import { BrazilGrowerDocuments } from '../components/BrazilGrowerDocuments/BrazilGrowerDocuments';
import { CreditAnalysis } from '../components/BrazilGrowerFinance/features/credit-analysis/CreditAnalysis/CreditAnalysis';
import { FieldMonitoringAnalysis } from '../components/BrazilGrowerFinance/features/field-monitoring-analysis/FieldMonitoringAnalysis/FieldMonitoringAnalysis';
import { DecisionModal } from '../components/BrazilGrowerFinance/shared/DecisionModal/DecisionModal';
import { PriorityButton } from '../components/BrazilGrowerFinance/shared/Priority/PriorityButton';
import { BrazilTimeline } from '../components/BrazilTimeline';
import { ConversationPage } from '../components/Conversations/ConversationPage';
import { ExternalLegalTimeline } from '../components/ExternalLegalTimeline/ExternalLegalTimeline';
import { ContractSection } from '../components/FullOrderDetail/ContractSection/ContractSection';
import { FullOrderDetail } from '../components/FullOrderDetail/FullOrderDetail';
import { Timeline } from '../components/Timeline/Timeline';
import { closeNegotiation, financingAgFinancing } from '../queries';
import { getOpportunityContactRolesAndBackgroundCheckGroup } from '../queries/backgroundCheck.queries';
import { getCreditApprovalData } from '../queries/creditApproval.queries';

interface ContactRolesBackgroundChecksQueryResponse {
  opportunityContactRoles: OpportunityContactRole[];
  backgroundCheckGroup: BackgroundCheckGroup;
  serasaReport: SerasaReport;
  bacenReport: BacenReport;
  contactsRiskClassification: RiskClassificationResult;
}

interface IFinanceProcessContext {
  agFinancingId: AgFinancing['id'];
  accountId: FinancingBrazilGrowerLead['id'];
  baseRoute: string;
  orderInfo: AgFinancing['orderInfo']; // TODO: remove as agFinance now is fully passed
  canStartPhase2: AgFinancing['canStartPhase2']; // TODO: remove as agFinance now is fully passed
  collateralType: AgFinancing['collateralType']; // TODO: remove as agFinance now is fully passed
  opportunitySaleType: AgFinancing['opportunitySaleType']; // TODO: remove as agFinance now is fully passed
  opportunityType: AgFinancing['opportunityType']; // TODO: remove as agFinance now is fully passed
  agFinance?: AgFinancing;
  growerInfo?: FinancingBrazilGrowerLead;
  contactRolesBackgroundChecksQuery?: QueryResult<
    ContactRolesBackgroundChecksQueryResponse,
    { opportunityId: string | undefined }
  >;
  creditApprovalDataQuery?: QueryResult<
    { creditApprovalData: CreditApprovalData },
    { agFinancingId: string | undefined }
  >;
  importedExceptions: string[];
  importedConditionals: string[];
  parentId: string;
}

export const FinanceProcessContext = React.createContext<IFinanceProcessContext>({
  agFinancingId: '',
  accountId: '',
  baseRoute: '',
  orderInfo: undefined,
  canStartPhase2: false,
  collateralType: CollateralType.NOT_FOUND,
  opportunitySaleType: OpportunitySaleType.NOT_SET,
  opportunityType: OpportunityType.NOT_SET,
  agFinance: undefined,
  growerInfo: undefined,
  contactRolesBackgroundChecksQuery: undefined,
  creditApprovalDataQuery: undefined,
  importedExceptions: [],
  importedConditionals: [],
  parentId: ''
});

interface TabConfig {
  label: string;
  url: string;
  permissions?: Permissions[];
}

interface BrazilGrowerDetailProps {
  accountId: FinancingBrazilGrowerLead['id'];
  growerInfo: FinancingBrazilGrowerLead;
  financingAgFinancing: AgFinancing;
}

const BrazilGrowerDetail: React.FunctionComponent<BrazilGrowerDetailProps> = ({
  accountId,
  financingAgFinancing,
  growerInfo
}) => {
  /**
   * State
   */
  const [tab, setTab] = useState<string>('/agFinance');

  /**
   * Routes Matching
   */
  const match = useRouteMatch();

  /**
   * History
   */
  const history = useHistory();

  /**
   * User Context
   */
  const userContext = useContext(UserContext);
  const isInternal =
    userContext.hasPermission(Permissions.FA_INTERNAL) || userContext.hasPermission(Permissions.FA_ADMIN);
  const isExternal = userContext.hasPermission(Permissions.FA_EXTERNAL);
  const isLegal = userContext.hasPermission(Permissions.FA_EXTERNAL_LEGAL);
  const isCredit = userContext.hasPermission(Permissions.FA_EXTERNAL_CREDIT);
  const isTerramagna = userContext.hasPermission(Permissions.FA_EXTERNAL_FIELD_MONITORING);

  /**
   * Tabs config
   */
  const tabs: TabConfig[] = [
    { label: 'agFinance', url: '/agFinance', permissions: [Permissions.FA_INTERNAL, Permissions.FA_ADMIN] },
    {
      label: 'contacts',
      url: '/contacts',
      permissions: [
        Permissions.FA_EXTERNAL_LEGAL,
        Permissions.FA_EXTERNAL_CREDIT,
        Permissions.FA_ADMIN,
        Permissions.FA_INTERNAL
      ]
    },
    { label: 'execution', url: '/execution', permissions: [Permissions.FA_EXTERNAL] },
    { label: 'conversations', url: '/conversations' },
    {
      label: 'documents',
      url: '/documents',
      permissions: [
        Permissions.FA_EXTERNAL_LEGAL,
        Permissions.FA_EXTERNAL_CREDIT,
        Permissions.FA_ADMIN,
        Permissions.FA_INTERNAL
      ]
    },
    {
      label: 'opportunities',
      url: '/order',
      permissions: [
        Permissions.FA_EXTERNAL_LEGAL,
        Permissions.FA_ADMIN,
        Permissions.FA_INTERNAL,
        Permissions.FA_EXTERNAL_FIELD_MONITORING,
        Permissions.FA_EXTERNAL_CREDIT
      ]
    },
    {
      label: 'historical',
      url: '/historical',
      permissions: [Permissions.FA_EXTERNAL_LEGAL, Permissions.FA_ADMIN, Permissions.FA_INTERNAL]
    },
    {
      label: 'Timeline',
      url: '/timeline',
      permissions: [
        Permissions.FA_EXTERNAL_LEGAL,
        Permissions.FA_EXTERNAL_CREDIT,
        Permissions.FA_ADMIN,
        Permissions.FA_INTERNAL
      ]
    }
  ];

  const handleTabChange = (selectedTab: string) => {
    history.push(`${match.url}${selectedTab}`);
  };

  const filteredTabs = tabs.filter(tab =>
    tab.permissions ? (tab.permissions.some(p => userContext.hasPermission(p)) ? true : false) : true
  );
  const redirectTo = isInternal ? 'agFinance' : isExternal ? 'execution' : 'contacts';

  const opportunityId = financingAgFinancing.orderInfo?.opportunities?.[0].id;
  const contactRolesBackgroundChecksQuery = useQuery(getOpportunityContactRolesAndBackgroundCheckGroup, {
    variables: { opportunityId },
    skip: !opportunityId || !(isInternal || isCredit || isLegal)
  });
  const creditApprovalDataQuery = useQuery(getCreditApprovalData, {
    variables: { agFinancingId: financingAgFinancing.id },
    skip: !isInternal
  });

  const urlTab = filteredTabs.find(tab => new RegExp(match.url + tab.url, 'i').test(history.location.pathname));

  if (urlTab && tab !== urlTab.url) {
    setTab(urlTab.url);
  }

  return financingAgFinancing && financingAgFinancing.id ? (
    <FinanceProcessContext.Provider
      value={{
        agFinancingId: financingAgFinancing.id,
        accountId,
        baseRoute: match.url,
        orderInfo: financingAgFinancing.orderInfo,
        canStartPhase2: financingAgFinancing.canStartPhase2,
        collateralType: financingAgFinancing.collateralType,
        opportunitySaleType: financingAgFinancing.opportunitySaleType,
        opportunityType: financingAgFinancing.opportunityType,
        agFinance: financingAgFinancing,
        growerInfo,
        contactRolesBackgroundChecksQuery,
        creditApprovalDataQuery,
        importedExceptions: financingAgFinancing.importedExceptions ?? [],
        importedConditionals: financingAgFinancing.importedConditionals ?? [],
        parentId: financingAgFinancing.parentId ?? ''
      }}
    >
      <div style={{ marginBottom: 34 }}>
        <Tabs
          activeTabId={tab}
          ariaLabel="AgFinancing Tabs"
          onChange={handleTabChange}
          items={filteredTabs.map(tab => ({ id: tab.url, title: i18next.t(tab.label), url: tab.url }))}
        />
      </div>
      <Switch>
        <Redirect exact from={match.url} to={`${match.url}/${redirectTo}`} />
        {!isTerramagna ? (
          <Route path={`${match.url}/contacts`}>
            <BrazilGrowerGeneralInformation />
          </Route>
        ) : null}
        {!isTerramagna ? (
          <Route path={`${match.url}/documents`}>
            <BrazilGrowerDocuments />
          </Route>
        ) : null}
        <Route path={`${match.url}/conversations/:tabId?/:conversationId?`}>
          <ConversationPage agFinancingId={financingAgFinancing.id} />
        </Route>
        {isInternal ? (
          <Route path={`${match.url}/agFinance`}>
            <Timeline />
          </Route>
        ) : null}
        {isExternal ? (
          <Route path={`${match.url}/execution`}>
            {isLegal ? (
              <ExternalLegalTimeline financingAgFinancing={financingAgFinancing} />
            ) : isTerramagna ? (
              <FieldMonitoringAnalysis fieldMonitoringData={financingAgFinancing.phase2?.fieldMonitoring} />
            ) : (
              <CreditAnalysis credit={financingAgFinancing.phase2?.credit} />
            )}
          </Route>
        ) : null}
        {isLegal || isInternal || isTerramagna || isCredit ? (
          <Route path={`${match.url}/order`}>
            <FullOrderDetail
              orderInfo={financingAgFinancing.orderInfo}
              creditAnalystAssigned={financingAgFinancing.creditAnalystAssigned}
              salesforceId={financingAgFinancing.grower?.salesforceId || ''}
            />
          </Route>
        ) : null}
        {isLegal || isInternal ? (
          <Route path={`${match.url}/historical`}>
            <ContractSection financingId={financingAgFinancing.id} />
          </Route>
        ) : null}
        {!isTerramagna ? (
          <Route path={`${match.url}/timeline`}>
            <BrazilTimeline />
          </Route>
        ) : null}
      </Switch>
    </FinanceProcessContext.Provider>
  ) : (
    <LoadingIndicator />
  );
};

export const BrazilGrowerDetailPage: React.FunctionComponent = () => {
  /**
   * Route Param
   */
  const { id } = useParams<{ id: string }>();

  /**
   * Mutations
   */
  const [closeNegotiationFn] = useMutation(closeNegotiation);

  /**
   * state
   */
  const [isDecisionOpen, setIsDecisionOpen] = useState(false);
  const [isClosingNegotiation, setIsClosingNegotiation] = useState(false);

  /**
   * User Context
   */
  const { isAdmin, isCreditAnalyst, isCreditLead, isSuper } = useContext(UserContext);

  const backURL = sessionStorageUtils.get('negotiationBackLink');

  useEffect(() => {
    return () => {
      sessionStorageUtils.remove('negotiationBackLink');
    };
  }, []);

  const { addFlashMessage } = useFlashMessage();
  const handleCloseNegotiation = (reason: string) => {
    setIsClosingNegotiation(true);

    return closeNegotiationFn({
      variables: { agFinancingId: id, reason }
    })
      .then(() => {
        addFlashMessage({
          content: text.firstLetterUppercase(i18next.t('negotiationClosed')),
          title: '',
          variant: 'success'
        });
      })
      .catch(() => {
        addFlashMessage({
          content: i18next.t('error'),
          title: '',
          variant: 'error'
        });
      })
      .finally(() => {
        setIsClosingNegotiation(false);
      });
  };

  const isNegotioationClosed = (data: AgFinancing) => {
    let isClosed = false;
    if (data && data.currentStates) {
      isClosed = data.currentStates[0] === AgStateName.CLOSED;
    }
    return isClosed;
  };

  const canChangePriority = isAdmin || isCreditAnalyst || isCreditLead;
  return (
    <LoadingWrapper
      query={financingAgFinancing}
      queryOptions={{
        fetchPolicy: 'network-only', // Used for first execution
        nextFetchPolicy: 'cache-first', // Used for subsequent executions
        variables: { agFinancingId: id }
      }}
    >
      {({ data, error }: { data: { financingAgFinancing: AgFinancing }; error?: ApolloError }) => {
        const financingBrazilGrowerLead: FinancingBrazilGrowerLead | undefined = data?.financingAgFinancing?.grower;

        if (error?.message || !financingBrazilGrowerLead) {
          return (
            <NewPage backLink="/br-growers" backLinkLabel="Growers" title="">
              {error?.message.includes('Unable to find') ? i18next.t('noGrowerFound') : i18next.t('error')}
            </NewPage>
          );
        }

        return (
          <NewPage
            backLink={backURL ? backURL : '/br-growers'}
            backLinkLabel={backURL ? i18next.t('goBack') : i18next.t('contracts')}
            title={financingBrazilGrowerLead.name}
            contentRight={
              <>
                <div className="flex">
                  {canChangePriority && <PriorityButton prioritySince={data?.financingAgFinancing.prioritySince} />}
                  {isSuper && !isNegotioationClosed(data?.financingAgFinancing) && (
                    <>
                      <Button
                        busy={isClosingNegotiation}
                        onClick={() => setIsDecisionOpen(true)}
                        size="small"
                        variant="primary"
                      >
                        {text.toTitleCase(i18next.t('closeNegotiation'))}
                      </Button>
                      <DecisionModal
                        closeModal={() => setIsDecisionOpen(false)}
                        message={text.firstLetterUppercase(i18next.t('closeNegotiationDisclaimer'))}
                        onConfirmAction={handleCloseNegotiation}
                        open={isDecisionOpen}
                        title={text.toTitleCase(i18next.t('closeNegotiation'))}
                      />
                    </>
                  )}
                </div>
              </>
            }
          >
            <BrazilGrowerDetail
              accountId={financingBrazilGrowerLead.id}
              financingAgFinancing={data.financingAgFinancing}
              growerInfo={financingBrazilGrowerLead}
            />
          </NewPage>
        );
      }}
    </LoadingWrapper>
  );
};
